/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as log from "loglevel";
import {UiRelativeWorkSpaceViewPosition} from "../../generated/UiRelativeWorkSpaceViewPosition";
import {DeferredExecutor} from "../util/DeferredExecutor";
import {UiToolbarConfig} from "../../generated/UiToolbarConfig";
import {UiComponentConfig} from "../../generated/UiComponentConfig";
import {UiWorkSpaceLayoutViewConfig} from "../../generated/UiWorkSpaceLayoutViewConfig";
import {ViewInfo} from "./ViewInfo";
import {UiWorkSpaceLayout, UiWorkspaceLayoutSubWindowProtocol_INIT_OK} from "./UiWorkSpaceLayout";
import {UiSplitSizePolicy} from "../../generated/UiSplitSizePolicy";
import {generateUUID} from "../Common";
import {ViewContainer, ViewContainerListener} from "./ViewContainer";
import {UiComponent} from "../UiComponent";
import {UiEvent} from "../../generated/UiEvent";
import {UiWorkSpaceLayoutItemConfig} from "../../generated/UiWorkSpaceLayoutItemConfig";
import {TeamAppsUiContext} from "../TeamAppsUiContext";
import {WindowLayoutDescriptor} from "./WindowLayoutDescriptor";

export interface ChildWindowViewContainerListener extends ViewContainerListener {
	handleInitialized(windowId: string, initialViewInfo: ViewInfo): void;

	handleUiEvent(uiEvent: UiEvent, redundancyKey: string): void;

	handleClosed(childWindowViewContainer: ChildWindowViewContainer): void;
}


interface RemoteMethodInvocationCallback {
	resolve: (x: any) => void,
	reject: (x: any) => void
}

export class ChildWindowViewContainer implements ViewContainer {

	private static logger: log.Logger = log.getLogger("UiWorkSpaceLayout.ChildWindowViewContainer");

	private _viewInfos: { [viewName: string]: ViewInfo } = {};
	private deferredExecutor: DeferredExecutor = new DeferredExecutor();
	private methodInvocationResponseCallbacksByCorrelationId: { [correlationId: string]: RemoteMethodInvocationCallback } = {};

	constructor(private childWindow: Window,
	            public readonly windowId: string,
	            private port: MessagePort,
	            private initialViewInfo: ViewInfo,
	            private owner: UiWorkSpaceLayout,
	            private context: TeamAppsUiContext,
	            private newWindowBackgroundImage: string,
	            private newWindowBlurredBackgroundImage: string,
	            private listener: ChildWindowViewContainerListener) {
		port.onmessage = (e) => {
			this.handlePortMessage(e, port, windowId);
		};
		childWindow.addEventListener('unload', () => {
			this.listener.handleClosed(this);
		});
	}

	public get viewNames() {
		return Object.keys(this._viewInfos);
	}

	public getViewInfo(viewName: string) {
		return this._viewInfos[viewName];
	}

	private handlePortMessage(e: MessageEvent, port: MessagePort, childWindowId: string) {
		ChildWindowViewContainer.logger.debug(e.data);
		if (e.data._type === 'INIT') {
			port.postMessage(JSON.stringify(<UiWorkspaceLayoutSubWindowProtocol_INIT_OK> {
				_type: 'INIT_OK',
				sessionId: this.context.sessionId,
				workspaceLayoutId: this.owner.getId(),
				windowId: childWindowId,
				uiConfiguration: {
					...this.context.config,
					backgroundImage: this.newWindowBackgroundImage,
					blurredBackgroundImage: this.newWindowBlurredBackgroundImage
				},
				childWindowPageTitle: 'Applicaiton subview' // TODO
			}));
			this.deferredExecutor.ready = true;
			this.listener.handleInitialized(this.windowId, this.initialViewInfo);
		} else if (e.data._type === 'VIEW_DROPPED') {
			this.listener.handleViewDroppedFromOtherWindow(e.data.sourceWindowId, e.data.targetWindowId, e.data.viewInfo, e.data.existingViewName, e.data.relativePosition);
		} else if (e.data._type === 'LOCAL_LAYOUT_CHANGED_BY_USER') {
			this.listener.handleLocalLayoutChangedByUser(e.data.windowId);
		} else if (e.data._type === 'REGISTER_COMPONENT') {
			const componentId = e.data.componentId;
			this.context.registerComponent(new Proxy({}, {
				get: (target, property, receiver) => {
					if (property === 'getId') {
						return () => componentId;
					} else {
						let me = this;
						return function () {
							me.sendCommandToWindow(property.toString(), arguments, componentId);
						}
					}
				}
			}) as UiComponent<UiComponentConfig>);
		} else if (e.data._type === 'EVENT') {
			this.listener.handleUiEvent(e.data.eventObject, e.data.redundancyKey);
		} else if (e.data._type === 'CHILD_WINDOW_CREATED') {
			let grandChildWindowId = e.data.childWindowId;
			let grandChildWindoMessagePort = e.ports[0];
			let viewInfo = e.data.viewInfo;
			this.listener.handleChildWindowCreated(grandChildWindowId, grandChildWindoMessagePort, viewInfo);
		} else if (e.data._type === 'CHILD_WINDOW_CREATION_FAILED') {
			let viewName = e.data.viewName;
			this.listener.handleChildWindowCreationFailed(viewName);
		} else if (e.data._type === 'WORKSPACELAYOUT_METHOD_INVOCATION_RESPONSE') {
			let callback = this.methodInvocationResponseCallbacksByCorrelationId[e.data.correlationId];
			if (callback) {
				if (e.data.successful) {
					callback.resolve(e.data.returnValue);
				} else {
					callback.reject(e.data.returnValue);
				}
			}
		}
	}

	setToolbar(toolbar: UiToolbarConfig): void {
		this.sendCommandToWindow("setToolbar", arguments);
	}

	refreshViewComponent(viewName: string, component: UiComponentConfig): void {
		this.sendCommandToWindow("refreshViewComponent", arguments);
	}

	refreshView(viewName: string, component: UiComponentConfig, tabIcon: string, tabCaption: string, tabCloseable: boolean): void {
		this.sendCommandToWindow("refreshView", arguments);
	}

	addViewToTopLevel(newView: UiWorkSpaceLayoutViewConfig, windowId: string, relativePosition: UiRelativeWorkSpaceViewPosition, sizePolicy: UiSplitSizePolicy, referenceChildSize: number): void {
		this._viewInfos[newView.viewName] = newView;
		this.sendCommandToWindow("addViewToTopLevel", arguments);
	}

	addViewRelativeToOtherView(newView: UiWorkSpaceLayoutViewConfig, existingViewName: string, relativePosition: UiRelativeWorkSpaceViewPosition, sizePolicy: UiSplitSizePolicy, referenceChildSize: number): void {
		this._viewInfos[newView.viewName] = newView;
		this.sendCommandToWindow("addViewRelativeToOtherView", arguments);
	}

	addViewAsTab(newView: UiWorkSpaceLayoutViewConfig, existingViewName: string): void {
		this._viewInfos[newView.viewName] = newView;
		this.sendCommandToWindow("addViewAsTab", arguments);
	}

	removeView(viewName: string): void {
		delete this._viewInfos[viewName];
		this.sendCommandToWindow("removeView", arguments);
	}

	redefineLayout(windowId: string, newLayout: UiWorkSpaceLayoutItemConfig): void {
		this.sendCommandToWindow("redefineLayout", arguments);
	}

	moveViewToTopLevel(viewName: string, windowId: string, relativePosition: UiRelativeWorkSpaceViewPosition, sizePolicy: UiSplitSizePolicy, referenceChildSize: number): void {
		this.sendCommandToWindow("moveViewToTopLevel", arguments);
	}

	moveViewRelativeToOtherView(viewName: string, existingViewName: string, relativePosition: UiRelativeWorkSpaceViewPosition, sizePolicy: UiSplitSizePolicy, referenceChildSize: number): void {
		this.sendCommandToWindow("moveViewRelativeToOtherView", arguments);
	}

	moveViewToTab(viewName: string, existingViewName: string): void {
		this.sendCommandToWindow("moveViewToTab", arguments);
	}

	selectViewTab(viewName: string) {
		this.sendCommandToWindow("selectViewTab", arguments);
	}

	async getLayoutDescriptor(): Promise<WindowLayoutDescriptor> {
		return this.invokeMethod("getLocalLayout");
	}

	private sendCommandToWindow(methodName: string, args: IArguments | any[], targetComponentId = this.owner.getId()) {
		if (!this.deferredExecutor.ready) {
			ChildWindowViewContainer.logger.debug(`Enqueueing method invocation to child window (${this.windowId}): ${methodName}(${args})`)
		}
		this.deferredExecutor.invokeWhenReady(() => {
			ChildWindowViewContainer.logger.debug(`Sending method invocation to child window (${this.windowId}): ${methodName}(${args})`);
			this.port.postMessage(JSON.stringify({
				_type: "COMMAND",
				componentId: targetComponentId,
				methodName: methodName,
				arguments: Array.prototype.slice.call(args)
			}));
		});
	}

	private async invokeMethod(methodName: string, ...args: any[]): Promise<any> {
		let correlationId = generateUUID();
		return await this.deferredExecutor.invokeWhenReady(() => {
			this.port.postMessage(JSON.stringify({
				_type: "WORKSPACELAYOUT_METHOD_INVOCATION",
				methodName: methodName,
				arguments: Array.prototype.slice.call(args),
				correlationId: correlationId
			}));
			return new Promise((resolve, reject) => {
				this.methodInvocationResponseCallbacksByCorrelationId[correlationId] = {resolve, reject}
			});
		});
	}
}